import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Gauge, Receipt, Umbrella, ClipboardText as ClipboardCheck, PiggyBank, Wrench, Fire as Flame, SquaresFour as LayoutDashboard, ChatCircle as MessageCircle, BookOpenText as BookOpen, ArrowsClockwise as RefreshCw } from "@phosphor-icons/react";
import { SiteNav } from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import { RealisticPhone, RealisticLaptop } from '../components/DeviceFrames';

const INK = '#2A1E5C';
const INK2 = '#5D5A6B';
const GOLD = '#F5CB5C';
const RED = '#DE3C4B';
const GREEN = '#3E8E4E';
const CREAM = '#FDFBF7';
const PAPER = '#FAF1D8';
const MUTED = '#8B7E6A';

const FEATURES = [
  {
    icon: Target, title: 'Find Your Relay Target',
    body: 'Start with the Relay Calculator and build your Income Map: your weekly relay target, daily focus number, savings goal, debt payoff goal, PTO plan, and tax set-aside percentage. Instead of carrying a giant annual goal in your head, you get one relay at a time.',
  },
  {
    icon: Gauge, title: 'Run The Week In Red, Black, And Green',
    body: 'Each relay starts in red: the distance between where you are and what the week needs from you. As you log income, the gap shrinks. When you clear your target, you hit black. When you pass it, you move into green. The color shift gives your work a finish line you can actually feel.',
    lanes: true,
  },
  {
    icon: Receipt, title: 'Build Your Tax Set-Aside Rhythm',
    body: 'Payday Relay does not calculate your taxes or replace a tax professional. It helps you build the habit of moving money toward taxes as you earn, based on the percentage you choose. Each relay gives you a clearer handoff: tax set-aside first, then goals, then take-home pay.',
  },
  {
    icon: Umbrella, title: 'Create A PTO Bank',
    body: 'Most independent workers do not get paid time off unless they create it. Payday Relay helps you treat rest as part of the plan. Your PTO Bank gives you a way to set money aside for sick days, slow weeks, and actual time off without pretending rest is free.',
  },
  {
    icon: ClipboardCheck, title: 'Close The Relay',
    body: 'At the end of each week, Payday Relay helps you compare the plan to reality. What did the system suggest you set aside? What did you actually move? Taxes, PTO, debt, savings, and take-home pay all get their own lane, so your money has somewhere to go before it disappears.',
  },
  {
    icon: PiggyBank, title: 'Grow Your Bonus Bank',
    body: 'The Bonus Bank rewards consistency, not random hustle spikes. It only grows when you finish a month net positive, after the month has done its job. That means the goal is not to overwork for one heroic week. The goal is to keep clearing your relays often enough that your surplus starts to build.',
  },
  {
    icon: Wrench, title: 'Hit Quarterly Tax Days',
    body: 'Life changes when you work for yourself. Payday Relay gives you quarterly tax days where you can review your rhythm, adjust workdays, revisit your target, and keep going with structure instead of starting from scratch every time life shifts.',
  },
  {
    icon: Flame, title: 'Keep The Streak Alive',
    body: 'Payday Relay uses momentum on purpose. Clearing a relay feels good. Watching red shrink to black, then black turn green, feels good. Seeing your streak build feels good. The system gives your brain a reward loop around consistency, not burnout.',
  },
  {
    icon: LayoutDashboard, title: 'See The Full Season',
    body: 'Behind the app sits your Payday Relay Google Sheet, generated for you when you sign up, in your own Drive. You never type in it; the app syncs it for you. It holds the full picture: 52 relays cleared, monthly surplus, PTO bank, Bonus Bank, quarterly targets, and your season scoreboard. Live in the app day to day while the sheet holds the year.',
  },
  {
    icon: MessageCircle, title: 'Let Caddy Coach The Rhythm',
    body: 'Caddy is the coach inside Payday Relay: a reflection prompt, a nudge, and a steady voice when the week starts feeling slippery. Caddy helps you stay close to the number you already chose.',
  },
];

const LANES = [
  { label: 'Red', note: 'still short of the week', color: RED },
  { label: 'Black', note: 'target cleared', color: '#1B1830' },
  { label: 'Green', note: 'past your number', color: GREEN },
];

const CtaPair = ({ dark = false, idPrefix }) => (
  <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
    <Link
      to="/start/free-sprint"
      className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold text-base sm:text-lg transition-transform hover:scale-[1.03] shadow-lg"
      style={{ background: GOLD, color: INK }}
      data-testid={`${idPrefix}-sprint-cta`}
    >
      Start Your Free Sprint <ArrowRight className="w-5 h-5" />
    </Link>
    <Link
      to="/method"
      className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold text-base sm:text-lg border-2 transition-colors"
      style={
        dark
          ? { borderColor: 'rgba(255,255,255,0.45)', color: '#FFFFFF' }
          : { borderColor: INK, color: INK }
      }
      data-testid={`${idPrefix}-method-cta`}
    >
      See The Rules Of The Race <BookOpen className="w-5 h-5" />
    </Link>
  </div>
);

const FeatureBlock = ({ item, index }) => {
  const Icon = item.icon;
  return (
    <div
      className="relative pl-14 sm:pl-16 pb-10"
      data-testid={`feature-block-${index}`}
      style={{ borderLeft: index === FEATURES.length - 1 ? 'none' : '2px dashed rgba(42,30,92,0.16)' }}
    >
      <span
        className="absolute -left-[22px] top-0 w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: CREAM, border: `2px solid ${GOLD}`, boxShadow: '0 4px 14px rgba(42,30,92,0.12)' }}
      >
        <Icon className="w-5 h-5" style={{ color: INK }} strokeWidth={2.1} />
      </span>
      <h2 className="text-xl sm:text-2xl leading-snug mb-3" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }}>
        {item.title}
      </h2>
      <p className="text-base leading-relaxed" style={{ color: INK2 }}>{item.body}</p>

      {item.lanes && (
        <div className="flex flex-wrap gap-2.5 mt-5">
          {LANES.map((l) => (
            <span
              key={l.label}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold"
              style={{ background: l.color, color: '#FFFFFF' }}
            >
              {l.label}
              <span className="font-normal" style={{ color: 'rgba(255,255,255,0.75)' }}>{l.note}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default function FeaturesPage() {
  useEffect(() => {
    document.title = 'Payday Relay Features, A Weekly Finish Line';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        'content',
        'Payday Relay features: relay targets, red-black-green weeks, tax set-aside rhythm, PTO bank, Bonus Bank, quarterly tax days, streaks, an auto-synced Google Sheet, and Caddy the coach.'
      );
    }
  }, []);

  return (
    <div className="min-h-screen" style={{ background: CREAM }} data-testid="features-page">
      <SiteNav />

      {/* ============ HERO ============ */}
      <section className="pt-14 sm:pt-20 pb-14 sm:pb-16 px-5 sm:px-8" style={{ background: INK }} data-testid="features-hero">
        <div className="max-w-6xl mx-auto lg:flex lg:items-center lg:gap-12">
          <div className="lg:w-[58%]">
            <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] mb-4" style={{ color: GOLD }}>
              What's inside Payday Relay
            </p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mb-6" style={{ fontFamily: "'Fraunces', Georgia, serif", color: '#FFFFFF' }} data-testid="features-headline">
              More than a money tracker. A weekly finish line.
            </h1>
            <p className="text-base sm:text-lg leading-relaxed mb-9" style={{ color: 'rgba(255,255,255,0.82)' }}>
              Payday Relay helps 1099 earners turn unstructured income into a weekly rhythm: what to aim
              for, what to set aside, when to stop, and how to keep moving through the year without
              turning every day into a workday.
            </p>
            <CtaPair dark idPrefix="features-hero" />
          </div>
          <div className="hidden lg:flex lg:w-[42%] flex-col items-center">
            <img
              src="/images/home-mockup-map.png"
              alt="Your Relay Target results, weekly target and 12-month path"
              className="w-full max-w-[430px] h-auto"
              loading="lazy"
            />
            <div
              className="mt-4 max-w-[400px] rounded-2xl px-5 py-4 text-left"
              style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(245,203,92,0.35)' }}
              data-testid="features-hero-image-label"
            >
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] mb-1.5" style={{ color: GOLD }}>
                What you're looking at
              </p>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.85)' }}>
                Your <b style={{ color: '#FFFFFF' }}>Relay Target results</b>, what the Relay Calculator
                inside Payday Relay emails you as your own PDF. Yours to keep, for good.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FEATURE LANES ============ */}
      <section className="py-14 sm:py-20 px-5 sm:px-8" data-testid="features-list">
        <div className="max-w-3xl mx-auto pl-2 sm:pl-0">
          {FEATURES.map((item, i) => (
            <FeatureBlock key={item.title} item={item} index={i} />
          ))}
        </div>
      </section>

      {/* ============ THE APP + THE SHEET ============ */}
      <section className="py-14 sm:py-16 px-5 sm:px-8" style={{ background: PAPER }} data-testid="features-shots">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl mb-4" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }}>
            The app runs your week. Your sheet holds your year.
          </h2>
          <p className="text-base max-w-2xl mx-auto mb-10" style={{ color: INK2 }}>
            Log from your phone in seconds. Your Payday Relay Google Sheet is generated for you when you
            sign up, then stays in sync with the app automatically, every relay, set-aside, and month summary
            written for you. You never open the sheet to type in it.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-8">
            <RealisticPhone
              src="/images/home-mockup-app-v2.png"
              alt="The Payday Relay app, what's left this week"
              className="w-[172px]"
              camera="punch"
              radius={2.1}
            />
            {/* Sync marker: makes the app-to-sheet connection visible */}
            <div className="flex flex-col items-center gap-2 shrink-0" data-testid="features-sync-icon">
              <span
                className="rounded-full flex items-center justify-center pr-sync-spin"
                style={{ width: 52, height: 52, background: GOLD, color: INK, boxShadow: '0 6px 18px rgba(42,30,92,0.14)' }}
              >
                <RefreshCw className="w-6 h-6" strokeWidth={2.5} />
              </span>
              <span className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: MUTED }}>
                Auto sync
              </span>
            </div>
            <RealisticLaptop
              src="/images/home-mockup-sheet.png"
              alt="Your Payday Relay Google Sheet, synced automatically from the app"
              className="w-full max-w-[420px]"
            />
          </div>
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="py-16 sm:py-20 px-5 sm:px-8" style={{ background: INK }} data-testid="features-cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl mb-5" style={{ fontFamily: "'Fraunces', Georgia, serif", color: '#FFFFFF' }} data-testid="features-cta-headline">
            Find your finish line
          </h2>
          <p className="text-base sm:text-lg leading-relaxed mb-9" style={{ color: 'rgba(255,255,255,0.82)' }} data-testid="features-cta-subhead">
            The free 4-Relay Sprint gives you four weeks to experience a clearer boundary between work and
            rest. If it fits your workflow, you can stick with it via a Season Pass.
          </p>
          <div className="flex justify-center">
            <CtaPair dark idPrefix="features-bottom" />
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
